import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { StatCard } from '@/components/base/StatCard';
import { SubscriptionMiniCard } from '@/components/base/SubscriptionMiniCard';
import {
  dashboardStats,
  dashboardAlerts,
  dailyRevenueChart,
  sourceDistribution,
  staffPerformance,
  serviceDistribution,
  todayHourlyDensity,
  tomorrowHourlyDensity,
} from '@/mocks/dashboard';
import { recentActivities } from '@/mocks/recentActivities';
import { todayAppointments } from '@/mocks/todayAppointments';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area,
} from 'recharts';

const statCards = [
  { title: 'Bugünkü Randevu', value: dashboardStats.todayAppointments, subtitle: 'randevu planlı', icon: 'ri-calendar-check-line', iconBg: 'rgba(201,169,98,0.12)', iconColor: '#C9A962', link: '/randevular' },
  { title: 'Yarınki Randevu', value: dashboardStats.tomorrowAppointments, subtitle: 'yarın için planlı', icon: 'ri-calendar-event-line', iconBg: 'rgba(59,130,246,0.12)', iconColor: '#3B82F6', link: '/randevular' },
  { title: 'Bugün Gelen', value: dashboardStats.todayCustomers, subtitle: 'müşteri geldi', icon: 'ri-user-follow-line', iconBg: 'rgba(16,185,129,0.12)', iconColor: '#10B981', link: '/musteriler' },
  { title: 'Gelmeyen', value: dashboardStats.noShowCustomers, subtitle: 'randevuya gelmedi', icon: 'ri-user-unfollow-line', iconBg: 'rgba(239,68,68,0.12)', iconColor: '#EF4444', link: '/randevular' },
  { title: 'Tahmini Ciro', value: `₺${dashboardStats.estimatedRevenue.toLocaleString('tr-TR')}`, subtitle: 'bugünkü tahmin', icon: 'ri-coins-line', iconBg: 'rgba(201,169,98,0.12)', iconColor: '#C9A962', link: '/kasa' },
  { title: 'Gerçekleşen Ciro', value: `₺${dashboardStats.actualRevenue.toLocaleString('tr-TR')}`, subtitle: 'bugünkü gerçekleşen', icon: 'ri-wallet-3-line', iconBg: 'rgba(45,95,62,0.12)', iconColor: '#2D5F3E', link: '/kasa' },
  { title: 'Açık Ödeme', value: `₺${dashboardStats.openPayment.toLocaleString('tr-TR')}`, subtitle: 'tahsilat bekleyen', icon: 'ri-error-warning-line', iconBg: 'rgba(239,68,68,0.12)', iconColor: '#EF4444', link: '/kasa' },
  { title: 'Aktif Paketli', value: dashboardStats.activePackageCustomers, subtitle: 'paketli müşteri', icon: 'ri-vip-crown-line', iconBg: 'rgba(139,92,246,0.12)', iconColor: '#8B5CF6', link: '/paketler' },
  { title: 'Seansı Azalan', value: dashboardStats.lowSessionPackages, subtitle: 'paket uyarısı', icon: 'ri-alarm-warning-line', iconBg: 'rgba(245,158,11,0.12)', iconColor: '#F59E0B', link: '/paketler' },
  { title: 'En Yoğun Saat', value: dashboardStats.peakHour, subtitle: 'bugünün zirvesi', icon: 'ri-time-line', iconBg: 'rgba(16,185,129,0.12)', iconColor: '#10B981', link: '/randevular' },
  { title: 'Google', value: dashboardStats.googleSource, subtitle: 'müşteri kaynağı', icon: 'ri-google-line', iconBg: 'rgba(45,95,62,0.12)', iconColor: '#2D5F3E', link: '/raporlar' },
  { title: 'Meta', value: dashboardStats.metaSource, subtitle: 'müşteri kaynağı', icon: 'ri-facebook-circle-line', iconBg: 'rgba(59,130,246,0.12)', iconColor: '#3B82F6', link: '/raporlar' },
  { title: 'WhatsApp', value: dashboardStats.whatsappSource, subtitle: 'müşteri kaynağı', icon: 'ri-whatsapp-line', iconBg: 'rgba(16,185,129,0.12)', iconColor: '#10B981', link: '/raporlar' },
  { title: 'Tekrar Müşteri', value: `%${dashboardStats.repeatRate}`, subtitle: 'sadakat oranı', icon: 'ri-heart-3-line', iconBg: 'rgba(201,169,98,0.12)', iconColor: '#C9A962', link: '/musteriler' },
];

const alertIcons: Record<string, string> = {
  confirmation: 'ri-question-answer-line',
  package: 'ri-gift-line',
  payment: 'ri-money-cny-circle-line',
  warning: 'ri-error-warning-line',
  panel: 'ri-information-line',
};

const alertColors: Record<string, { border: string; icon: string }> = {
  high: { border: '#EF4444', icon: '#EF4444' },
  medium: { border: '#F59E0B', icon: '#F59E0B' },
  low: { border: '#3B82F6', icon: '#3B82F6' },
};

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: 'Tamamlandı', color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
  in_progress: { label: 'Devam Ediyor', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  upcoming: { label: 'Yaklaşan', color: '#6B6560', bg: 'rgba(107,101,96,0.10)' },
  cancelled: { label: 'İptal', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  no_show: { label: 'Gelmedi', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
};

const paymentConfig: Record<string, { label: string; color: string }> = {
  paid: { label: 'Ödendi', color: '#10B981' },
  unpaid: { label: 'Bekliyor', color: '#F59E0B' },
  partial: { label: 'Kısmi', color: '#3B82F6' },
};

const quickActions = [
  { title: 'Randevu', icon: 'ri-add-circle-line', color: '#3B82F6', bg: 'rgba(59,130,246,0.10)', link: '/randevular' },
  { title: 'Ödeme Al', icon: 'ri-money-cny-circle-line', color: '#10B981', bg: 'rgba(16,185,129,0.10)', link: '/kasa' },
  { title: 'Müşteri', icon: 'ri-user-add-line', color: '#C9A962', bg: 'rgba(201,169,98,0.10)', link: '/musteriler' },
  { title: 'Gider', icon: 'ri-shopping-cart-line', color: '#EF4444', bg: 'rgba(239,68,68,0.10)', link: '/giderler' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const chartTextColor = theme === 'dark' ? '#8A8680' : '#6B6560';
  const chartGridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const chartLabelColor = theme === 'dark' ? '#E8E4DC' : '#1A1A1A';

  const filteredAppointments = activeFilter === 'all'
    ? todayAppointments
    : todayAppointments.filter((a) => a.status === activeFilter);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>
            Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Kuğu Spa günlük durum özeti
          </p>
        </div>
      </div>

      {/* Quick Actions Row */}
      <div className="grid grid-cols-4 gap-3">
        {quickActions.map((action) => (
          <button
            key={action.title}
            onClick={() => navigate(action.link)}
            className="spa-card p-3 md:p-4 flex flex-col items-center gap-2 cursor-pointer hover:scale-[1.02] transition-transform"
          >
            <div
              className="w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center"
              style={{ background: action.bg }}
            >
              <i className={`${action.icon} text-lg md:text-xl`} style={{ color: action.color }} />
            </div>
            <span className="text-xs md:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {action.title}
            </span>
          </button>
        ))}
      </div>

      {/* Widgets Row: Recent Activities + Today's Appointments */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Son İşlemler / Canlı Akış */}
        <div className="spa-card p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
              Son İşlemler
            </h3>
            <button
              onClick={() => navigate('/kasa')}
              className="text-xs cursor-pointer hover:underline flex items-center gap-1"
              style={{ color: 'var(--gold)' }}
            >
              <span>Tümünü Gör</span>
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
          <div className="space-y-0 max-h-[360px] overflow-y-auto pr-1">
            {recentActivities.map((activity, idx) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 py-3 relative"
                style={{
                  borderBottom: idx < recentActivities.length - 1 ? '1px solid var(--border-color)' : 'none',
                }}
              >
                {/* Timeline line */}
                <div className="absolute left-[19px] top-[42px] w-[2px] h-[calc(100%-24px)]" style={{ background: 'var(--border-color)' }} />
                {/* Icon */}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 relative z-10"
                  style={{ background: activity.iconBg }}
                >
                  <i className={`${activity.icon} text-base`} style={{ color: activity.iconColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {activity.title}
                    </p>
                    <span className="text-[11px] flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                      {activity.time}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {activity.description}
                  </p>
                  {activity.amount && (
                    <p className="text-xs font-semibold mt-0.5" style={{ color: activity.iconColor }}>
                      {activity.amount}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bugünkü Randevular */}
        <div className="spa-card p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
              Bugünkü Randevular
            </h3>
            <span
              className="text-xs font-medium px-2 py-1 rounded-full"
              style={{ background: 'rgba(201,169,98,0.12)', color: '#C9A962' }}
            >
              {todayAppointments.length} randevu
            </span>
          </div>

          {/* Status Filter */}
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { key: 'all', label: 'Tümü' },
              { key: 'completed', label: 'Tamamlandı' },
              { key: 'in_progress', label: 'Devam Ediyor' },
              { key: 'upcoming', label: 'Yaklaşan' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className="text-[11px] font-medium px-2.5 py-1 rounded-full cursor-pointer transition-all"
                style={{
                  background: activeFilter === filter.key ? 'var(--gold)' : 'var(--bg-tertiary)',
                  color: activeFilter === filter.key ? '#FFFFFF' : 'var(--text-secondary)',
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Appointments List */}
          <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1">
            {filteredAppointments.map((apt) => {
              const st = statusConfig[apt.status];
              const pay = paymentConfig[apt.paymentStatus];
              return (
                <div
                  key={apt.id}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: 'var(--bg-tertiary)' }}
                >
                  {/* Time */}
                  <div className="flex-shrink-0 text-center w-12">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {apt.time}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      {apt.duration}
                    </p>
                  </div>
                  {/* Divider */}
                  <div className="w-[2px] h-8 rounded-full flex-shrink-0" style={{ background: st.color + '40' }} />
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {apt.customer}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] truncate" style={{ color: 'var(--text-secondary)' }}>
                        {apt.service}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        • {apt.staff}
                      </span>
                    </div>
                  </div>
                  {/* Status & Amount */}
                  <div className="flex-shrink-0 text-right">
                    <span
                      className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full"
                      style={{ background: st.bg, color: st.color }}
                    >
                      {st.label}
                    </span>
                    <p className="text-xs font-semibold mt-1" style={{ color: pay.color }}>
                      {apt.amount}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredAppointments.length === 0 && (
            <div className="text-center py-8">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                <i className="ri-inbox-line text-lg" style={{ color: 'var(--text-secondary)' }} />
              </div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Bu filtreye uygun randevu bulunmuyor</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
        {statCards.map((card) => (
          <StatCard
            key={card.title}
            title={card.title}
            value={card.value}
            subtitle={card.subtitle}
            icon={card.icon}
            iconBg={card.iconBg}
            iconColor={card.iconColor}
            onClick={() => navigate(card.link)}
          />
        ))}
      </div>

      {/* Alerts Section */}
      <div className="space-y-3">
        <h2 className="text-base md:text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
          Uyarılar & Bildirimler
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <SubscriptionMiniCard />
          {dashboardAlerts.map((alert) => (
            <div
              key={alert.id}
              className="spa-card p-4 flex items-start gap-3 cursor-pointer"
              style={{ borderLeft: `3px solid ${alertColors[alert.priority].border}` }}
            >
              <div className="w-9 h-9 rounded-spa-sm flex items-center justify-center flex-shrink-0" style={{ background: alertColors[alert.priority].icon + '15' }}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${alertIcons[alert.type]} text-base`} style={{ color: alertColors[alert.priority].icon }} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{alert.title}</p>
                  {alert.count !== null && (
                    <span className="spa-badge flex-shrink-0" style={{ background: alertColors[alert.priority].icon + '15', color: alertColors[alert.priority].icon }}>
                      {alert.count}
                    </span>
                  )}
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{alert.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        {/* Daily Revenue Chart */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Günlük Ciro Takibi
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyRevenueChart} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="day" tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} />
                <YAxis tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} tickFormatter={(v) => `₺${(v / 1000)}K`} />
                <Tooltip
                  contentStyle={{
                    background: theme === 'dark' ? '#1A1A20' : '#FFFFFF',
                    border: `1px solid ${chartGridColor}`,
                    borderRadius: '8px',
                    color: chartLabelColor,
                  }}
                  formatter={(value: number) => [`₺${value.toLocaleString('tr-TR')}`, '']}
                />
                <Bar dataKey="estimated" name="Tahmini" fill="#C9A962" radius={[4, 4, 0, 0]} barSize={20} />
                <Bar dataKey="actual" name="Gerçekleşen" fill="#2D5F3E" radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Source Distribution */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Müşteri Kaynak Dağılımı
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sourceDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                  stroke="none"
                >
                  {sourceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: theme === 'dark' ? '#1A1A20' : '#FFFFFF',
                    border: `1px solid ${chartGridColor}`,
                    borderRadius: '8px',
                    color: chartLabelColor,
                  }}
                  formatter={(value: number) => [`%${value}`, '']}
                />
                <Legend
                  verticalAlign="middle"
                  align="right"
                  layout="vertical"
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 12, color: chartTextColor }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Staff Performance */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Personel Performansı
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={staffPerformance} layout="vertical" barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} horizontal={false} />
                <XAxis type="number" tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} tickFormatter={(v) => `₺${(v / 1000)}K`} />
                <YAxis type="category" dataKey="name" tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} width={70} />
                <Tooltip
                  contentStyle={{
                    background: theme === 'dark' ? '#1A1A20' : '#FFFFFF',
                    border: `1px solid ${chartGridColor}`,
                    borderRadius: '8px',
                    color: chartLabelColor,
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'revenue') return [`₺${value.toLocaleString('tr-TR')}`, 'Ciro'];
                    return [value, 'Randevu'];
                  }}
                />
                <Bar dataKey="revenue" name="revenue" fill="#C9A962" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Distribution */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Hizmet Satış Dağılımı
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={serviceDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {serviceDistribution.map((_entry, index) => {
                    const colors = ['#C9A962', '#2D5F3E', '#3B82F6', '#10B981', '#8B5CF6', '#9CA3AF'];
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: theme === 'dark' ? '#1A1A20' : '#FFFFFF',
                    border: `1px solid ${chartGridColor}`,
                    borderRadius: '8px',
                    color: chartLabelColor,
                  }}
                  formatter={(value: number) => [`%${value}`, '']}
                />
                <Legend
                  verticalAlign="middle"
                  align="right"
                  layout="vertical"
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 12, color: chartTextColor }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Today's Hourly Density */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Bugün Randevu Yoğunluğu
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={todayHourlyDensity}>
                <defs>
                  <linearGradient id="todayGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C9A962" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#C9A962" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="hour" tick={{ fill: chartTextColor, fontSize: 11 }} axisLine={{ stroke: chartGridColor }} />
                <YAxis tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} />
                <Tooltip
                  contentStyle={{
                    background: theme === 'dark' ? '#1A1A20' : '#FFFFFF',
                    border: `1px solid ${chartGridColor}`,
                    borderRadius: '8px',
                    color: chartLabelColor,
                  }}
                  formatter={(value: number) => [`${value} randevu`, '']}
                />
                <Area type="monotone" dataKey="count" stroke="#C9A962" fill="url(#todayGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tomorrow's Hourly Density */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Yarın Randevu Yoğunluğu
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={tomorrowHourlyDensity}>
                <defs>
                  <linearGradient id="tomorrowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2D5F3E" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2D5F3E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="hour" tick={{ fill: chartTextColor, fontSize: 11 }} axisLine={{ stroke: chartGridColor }} />
                <YAxis tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} />
                <Tooltip
                  contentStyle={{
                    background: theme === 'dark' ? '#1A1A20' : '#FFFFFF',
                    border: `1px solid ${chartGridColor}`,
                    borderRadius: '8px',
                    color: chartLabelColor,
                  }}
                  formatter={(value: number) => [`${value} randevu`, '']}
                />
                <Area type="monotone" dataKey="count" stroke="#2D5F3E" fill="url(#tomorrowGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}